import React, { useEffect, useMemo, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import ProfessionalReviewDisplay from '../components/profile/ProfessionalReviewDisplay';
import {
  agendaCountryOptions,
  getAgendaCountry,
  getTherapyModality,
  therapyModalityOptions,
} from '../constants/agendaTherapyTypes';
import { mapPatientFromApi } from '../constants/childInterests';
import {
  agendaCitaTipoLabel,
  enrichSpecialty,
  fetchAgendaSedes,
  fetchAgendaSpecialists,
  fetchAgendaSpecialistsPresencial,
  fetchAgendaSpecialties,
  fetchEspecialistaServicios,
  filterSpecialistsBySpecialty,
  formatAgendaPrecio,
} from '../services/agendaService';
import { getUserDisplayName, getUserInitials, getUserSession } from '../services/authService';
import {
  fetchPublishedPresencialReview,
  fetchPublishedProfessionalReview,
} from '../services/professionalReviewService';
import { fetchUserProfile } from '../services/profileService';
import '../styles/agenda.css';

const isCreatedPatient = (patient) =>
  Boolean(
    patient &&
      String(patient.nombre || '').trim() &&
      String(patient.documentoNumero || '').trim()
  );

const AgendaBookingPage = () => {
  const history = useHistory();
  const sessionUser = getUserSession();
  const sessionUserId = sessionUser ? sessionUser.id : null;
  const isRepresentante = sessionUser?.tipo === 'paciente';
  const isEspecialista = sessionUser?.tipo === 'especialista';

  const [selectedModality, setSelectedModality] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedSede, setSelectedSede] = useState('');
  const [selectedSpecialty, setSelectedSpecialty] = useState('');
  const [selectedSpecialist, setSelectedSpecialist] = useState('');
  const [showingSpecialists, setShowingSpecialists] = useState(false);

  const [patients, setPatients] = useState([]);
  const [loadingPatients, setLoadingPatients] = useState(false);
  const [patientsError, setPatientsError] = useState('');

  const [specialties, setSpecialties] = useState([]);
  const [specialists, setSpecialists] = useState([]);
  const [presencialSpecialists, setPresencialSpecialists] = useState([]);
  const [sedes, setSedes] = useState([]);

  const [loadingSpecialties, setLoadingSpecialties] = useState(false);
  const [loadingSpecialists, setLoadingSpecialists] = useState(false);
  const [loadingPresencialSpecialists, setLoadingPresencialSpecialists] = useState(false);
  const [loadingSedes, setLoadingSedes] = useState(false);

  const [specialtiesError, setSpecialtiesError] = useState('');
  const [specialistsError, setSpecialistsError] = useState('');
  const [sedesError, setSedesError] = useState('');

  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewData, setReviewData] = useState(null);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewError, setReviewError] = useState('');
  const [reviewTarget, setReviewTarget] = useState(null);

  const [serviciosOpen, setServiciosOpen] = useState(false);
  const [serviciosData, setServiciosData] = useState([]);
  const [serviciosLoading, setServiciosLoading] = useState(false);
  const [serviciosError, setServiciosError] = useState('');
  const [serviciosTarget, setServiciosTarget] = useState(null);

  const activeModality = getTherapyModality(selectedModality);
  const isOnline = selectedModality === 'online';
  const isPresencial = selectedModality === 'presencial';
  const isTherappy = selectedModality === 'therappy';
  const showBookingForm = isOnline || isPresencial;

  useEffect(() => {
    if (!isEspecialista || !sessionUserId) {
      return undefined;
    }

    const actual = getUserSession();

    history.replace({
      pathname: '/agenda/calendario',
      state: {
        modality: { id: 'online', label: 'Terapia Online' },
        specialist: {
          id: sessionUserId,
          label: getUserDisplayName(actual) || 'Especialista',
        },
      },
    });

    return undefined;
  }, [history, isEspecialista, sessionUserId]);

  useEffect(() => {
    if (!isRepresentante || !sessionUserId) {
      setPatients([]);
      setPatientsError('');
      return undefined;
    }

    setLoadingPatients(true);
    setPatientsError('');

    fetchUserProfile(sessionUserId)
      .then((data) => {
        const list = (data.hijos || data.pacientes || [])
          .map(mapPatientFromApi)
          .filter(isCreatedPatient);
        setPatients(list);
      })
      .catch((error) => {
        setPatients([]);
        setPatientsError(error.message || 'No se pudieron cargar tus pacientes.');
      })
      .finally(() => {
        setLoadingPatients(false);
      });

    return undefined;
  }, [isRepresentante, sessionUserId]);

  useEffect(() => {
    if (!showBookingForm) {
      return undefined;
    }

    setLoadingSpecialties(true);
    setSpecialtiesError('');

    fetchAgendaSpecialties()
      .then((list) => {
        const enriched = list.map(enrichSpecialty);
        setSpecialties(enriched);
        if (enriched.length > 0) {
          setSelectedSpecialty(String(enriched[0].id));
        }
      })
      .catch((error) => {
        setSpecialtiesError(error.message || 'No se pudieron cargar las especialidades.');
      })
      .finally(() => {
        setLoadingSpecialties(false);
      });

    if (isOnline) {
      setLoadingSpecialists(true);
      setSpecialistsError('');

      fetchAgendaSpecialists()
        .then((list) => {
          setSpecialists(list);
          if (list.length > 0) {
            setSelectedSpecialist(String(list[0].id));
          }
        })
        .catch((error) => {
          setSpecialistsError(error.message || 'No se pudieron cargar los especialistas.');
        })
        .finally(() => {
          setLoadingSpecialists(false);
        });
    } else {
      setSpecialists([]);
    }

    return undefined;
  }, [showBookingForm, isOnline]);

  useEffect(() => {
    if (!isPresencial || !selectedCountry) {
      setSedes([]);
      setSelectedSede('');
      setSedesError('');
      return undefined;
    }

    setLoadingSedes(true);
    setSedesError('');
    setSelectedSede('');
    setPresencialSpecialists([]);
    setSelectedSpecialist('');

    fetchAgendaSedes(selectedCountry)
      .then((list) => {
        setSedes(list);
        if (list.length > 0) {
          setSelectedSede(list[0].id);
        }
      })
      .catch((error) => {
        setSedes([]);
        setSedesError(error.message || 'No se pudieron cargar las sedes.');
      })
      .finally(() => {
        setLoadingSedes(false);
      });

    return undefined;
  }, [isPresencial, selectedCountry]);

  useEffect(() => {
    if (!isPresencial || !selectedSede || !selectedSpecialty) {
      if (isPresencial) {
        setPresencialSpecialists([]);
        setSelectedSpecialist('');
      }
      return undefined;
    }

    setLoadingPresencialSpecialists(true);
    setSpecialistsError('');
    setSelectedSpecialist('');

    fetchAgendaSpecialistsPresencial(selectedSede, selectedSpecialty)
      .then((list) => {
        setPresencialSpecialists(list);
        if (list.length > 0) {
          setSelectedSpecialist(String(list[0].id));
        }
      })
      .catch((error) => {
        setPresencialSpecialists([]);
        setSpecialistsError(
          error.message || 'No se pudieron cargar los especialistas de Terapia.'
        );
      })
      .finally(() => {
        setLoadingPresencialSpecialists(false);
      });

    return undefined;
  }, [isPresencial, selectedSede, selectedSpecialty]);

  const activeSede = useMemo(
    () => sedes.find((item) => String(item.id) === String(selectedSede)) || null,
    [sedes, selectedSede]
  );

  const availableSpecialists = useMemo(() => {
    if (isPresencial) {
      return presencialSpecialists;
    }

    return filterSpecialistsBySpecialty(specialists, selectedSpecialty);
  }, [isPresencial, presencialSpecialists, specialists, selectedSpecialty]);

  useEffect(() => {
    const stillAvailable = availableSpecialists.some(
      (item) => String(item.id) === String(selectedSpecialist)
    );

    if (!stillAvailable && availableSpecialists.length > 0) {
      setSelectedSpecialist(String(availableSpecialists[0].id));
    }

    if (availableSpecialists.length === 0) {
      setSelectedSpecialist('');
    }
  }, [availableSpecialists, selectedSpecialist]);

  const handleSelectModality = (modalityId) => {
    setSelectedModality(modalityId);
    setSelectedCountry('');
    setSelectedSede('');
    setSedes([]);
    setSedesError('');
    setPatientsError('');
    setPresencialSpecialists([]);
    setSelectedSpecialist('');
    setShowingSpecialists(false);
  };

  const handleChooseSpecialty = (specialtyId) => {
    setSelectedSpecialty(String(specialtyId));
    setShowingSpecialists(true);
  };

  const handleBackToSpecialties = () => {
    setShowingSpecialists(false);
  };

  const handleBackToModality = () => {
    setSelectedModality('');
    setSelectedCountry('');
    setSelectedSede('');
    setSedes([]);
    setSedesError('');
    setPatientsError('');
    setPresencialSpecialists([]);
    setSelectedSpecialist('');
    setShowingSpecialists(false);
  };

  const handleOpenReview = (specialist) => {
    if (!specialist || !specialist.tiene_resena_publicada) {
      return;
    }

    const cedulaPresencial = specialist.cedula || specialist.documento || '';
    const esPresencial = specialist.origen === 'terapia';

    if (esPresencial && !cedulaPresencial) {
      return;
    }

    setReviewTarget(specialist);
    setReviewOpen(true);
    setReviewLoading(true);
    setReviewError('');
    setReviewData(null);

    const solicitud = esPresencial
      ? fetchPublishedPresencialReview(cedulaPresencial)
      : fetchPublishedProfessionalReview(specialist.id);

    solicitud
      .then((data) => {
        setReviewData(data);
      })
      .catch((error) => {
        setReviewError(error.message || 'No se pudo cargar la reseña del especialista.');
      })
      .finally(() => {
        setReviewLoading(false);
      });
  };

  const handleCloseReview = () => {
    setReviewOpen(false);
    setReviewTarget(null);
    setReviewData(null);
    setReviewError('');
  };

  const handleOpenServicios = (specialist) => {
    if (!specialist) {
      return;
    }

    const specialty = specialties.find((item) => String(item.id) === String(selectedSpecialty));
    const especialidadCodigo = specialty ? specialty.codigo || specialty.id || '' : '';
    const esPresencial = specialist.origen === 'terapia';
    const cedulaPresencial = specialist.cedula || specialist.documento || '';

    setServiciosTarget(specialist);
    setServiciosOpen(true);
    setServiciosLoading(true);
    setServiciosError('');
    setServiciosData([]);

    fetchEspecialistaServicios({
      especialistaId: esPresencial ? null : specialist.id,
      terapiaDocumento: esPresencial ? cedulaPresencial : '',
      especialidadCodigo,
    })
      .then((lista) => {
        setServiciosData(Array.isArray(lista) ? lista : []);
      })
      .catch((error) => {
        setServiciosError(error.message || 'No se pudieron cargar los servicios.');
      })
      .finally(() => {
        setServiciosLoading(false);
      });
  };

  const handleCloseServicios = () => {
    setServiciosOpen(false);
    setServiciosTarget(null);
    setServiciosData([]);
    setServiciosError('');
  };

  const handleContinue = (specialistOverride) => {
    const specialty = specialties.find((item) => String(item.id) === String(selectedSpecialty));
    const specialist =
      specialistOverride ||
      availableSpecialists.find((item) => String(item.id) === String(selectedSpecialist));

    if (!specialist || !specialty) {
      return;
    }

    setSelectedSpecialist(String(specialist.id));
    setPatientsError('');

    if (isPresencial && !activeSede) {
      setSedesError('Selecciona una sede con oficina presencial.');
      return;
    }

    if (isRepresentante) {
      if (loadingPatients) {
        setPatientsError('Espera un momento mientras cargamos tus pacientes.');
        return;
      }

      if (patients.length === 0) {
        setPatientsError(
          'Debes registrar al paciente al que deseas generar la terapia o cita antes de continuar.'
        );
        return;
      }
    }

    const country = getAgendaCountry(selectedCountry);

    history.push({
      pathname: '/agenda/calendario',
      state: {
        modality: activeModality
          ? {
              id: activeModality.id,
              label: activeModality.label,
              brand: activeModality.brand,
            }
          : null,
        location: isPresencial
          ? {
              id: activeSede.id,
              label: activeSede.label,
              pais: selectedCountry,
              paisLabel: country ? country.label : selectedCountry,
            }
          : {
              id: 'online',
              label: 'Online',
            },
        specialty,
        specialist: {
          id: specialist.id,
          label: specialist.label,
          origen: specialist.origen || 'beyond',
          cedula: specialist.cedula || specialist.documento || null,
          sede: isPresencial ? selectedSede : null,
          vinculado_terapias: specialist.vinculado_terapias,
        },
      },
    });
  };

  const canScheduleSpecialist = (specialist) =>
    showBookingForm &&
    !isLoading &&
    !loadingPatients &&
    selectedSpecialty &&
    specialist &&
    (!isPresencial || (selectedCountry && selectedSede && sedes.length > 0));

  const getSpecialistInitials = (specialist) =>
    getUserInitials({
      nombre: specialist.nombre || specialist.label || '',
      apellido: specialist.apellido || '',
    });

  const isLoading =
    loadingSpecialties ||
    (isOnline && loadingSpecialists) ||
    (isPresencial && (loadingSedes || loadingPresencialSpecialists));

  const reviewAvatarUrl =
    (reviewData && reviewData.avatar_url) ||
    (reviewTarget && reviewTarget.avatar_url) ||
    null;

  const reviewSpecialistName = reviewTarget ? reviewTarget.label : '';

  const reviewSpecialistInitials = reviewTarget ? getSpecialistInitials(reviewTarget) : '';

  return (
    <div className="agenda-root agenda-root--booking">
      <section className="agenda-booking">
        <div className="agenda-booking-layout">
          <div className="agenda-booking-panel">
            {!selectedModality ? (
            <div className="agenda-booking-group">
              <p className="agenda-booking-label">Elige el tipo de terapia</p>
              <div className="agenda-modality-grid">
                {therapyModalityOptions.map((modality) => {
                  const isSelected = selectedModality === modality.id;

                  return (
                    <button
                      key={modality.id}
                      type="button"
                      className={`agenda-modality-card${
                        isSelected ? ' agenda-modality-card--selected' : ''
                      }`}
                      onClick={() => handleSelectModality(modality.id)}
                    >
                      <img
                        src={`${process.env.PUBLIC_URL}/images/${modality.image}`}
                        alt=""
                        className="agenda-modality-logo"
                      />
                      <strong>{modality.label}</strong>
                      <span>{modality.description}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            ) : (
            <>
            <button
              type="button"
              className="agenda-booking-back"
              onClick={handleBackToModality}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <path
                  d="M15 6l-6 6 6 6"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Tipo de terapia
            </button>

            {isTherappy ? (
              <div className="agenda-under-construction" role="status">
                <h2>TherAppy+</h2>
                <p>
                  Esta modalidad está en construcción. Pronto podrás acceder a terapias desde tu
                  celular guiadas por el avatar Lice.
                </p>
              </div>
            ) : null}

            {showBookingForm ? (
              <>
                {isPresencial ? (
                  <>
                    <div className="agenda-booking-group">
                      <label className="agenda-booking-field" htmlFor="agenda-country">
                        <span className="agenda-booking-label">Elige el país</span>
                        <select
                          id="agenda-country"
                          className="agenda-booking-select"
                          value={selectedCountry}
                          onChange={(event) => setSelectedCountry(event.target.value)}
                        >
                          <option value="">Selecciona un país</option>
                          {agendaCountryOptions.map((country) => (
                            <option key={country.id} value={country.id}>
                              {country.label}
                            </option>
                          ))}
                        </select>
                      </label>
                    </div>

                    {selectedCountry ? (
                      <div className="agenda-booking-group">
                        <label className="agenda-booking-field" htmlFor="agenda-sede">
                          <span className="agenda-booking-label">Elige la sede</span>
                          <select
                            id="agenda-sede"
                            className="agenda-booking-select"
                            value={selectedSede}
                            onChange={(event) => setSelectedSede(event.target.value)}
                            disabled={loadingSedes || sedes.length === 0}
                          >
                            {loadingSedes ? <option value="">Cargando sedes…</option> : null}
                            {!loadingSedes && sedes.length === 0 ? (
                              <option value="">No hay sedes en este país</option>
                            ) : null}
                            {sedes.map((sede) => (
                              <option key={sede.id} value={sede.id}>
                                {sede.label}
                              </option>
                            ))}
                          </select>
                        </label>

                        {!loadingSedes && selectedCountry && sedes.length === 0 ? (
                          <p
                            className="agenda-booking-message agenda-booking-message--warning"
                            role="status"
                          >
                            No hay oficinas presenciales disponibles para este país en Terapia. Por
                            ahora solo hay sedes donde exista atención presencial.
                          </p>
                        ) : null}

                        {sedesError ? (
                          <p
                            className="agenda-booking-message agenda-booking-message--error"
                            role="alert"
                          >
                            {sedesError}
                          </p>
                        ) : null}
                      </div>
                    ) : null}
                  </>
                ) : null}

                {(!isPresencial || (selectedCountry && sedes.length > 0)) ? (
                  <>
                    {!showingSpecialists ? (
                    <div className="agenda-booking-group">
                      <p className="agenda-booking-label" id="agenda-specialty-label">
                        Seleccione especialidad
                      </p>

                      {loadingSpecialties ? (
                        <p className="agenda-booking-message" role="status">
                          Cargando especialidades…
                        </p>
                      ) : null}

                      {!loadingSpecialties && specialties.length === 0 ? (
                        <p className="agenda-booking-message" role="status">
                          No hay especialidades disponibles
                        </p>
                      ) : null}

                      {specialties.length > 0 ? (
                        <div
                          className="agenda-specialty-row"
                          role="listbox"
                          aria-labelledby="agenda-specialty-label"
                        >
                          {specialties.map((specialty) => {
                            const isSelected = String(selectedSpecialty) === String(specialty.id);

                            return (
                              <button
                                key={specialty.id}
                                type="button"
                                role="option"
                                aria-selected={isSelected}
                                className={`agenda-specialty-card${
                                  isSelected ? ' agenda-specialty-card--selected' : ''
                                }`}
                                onClick={() => handleChooseSpecialty(specialty.id)}
                              >
                                <img
                                  src={`${process.env.PUBLIC_URL}/images/${specialty.image}`}
                                  alt=""
                                  className="agenda-specialty-card-photo"
                                />
                                <strong className="agenda-specialty-card-title">
                                  {specialty.label}
                                </strong>
                                <span className="agenda-specialty-card-badge">TERAPIA</span>
                              </button>
                            );
                          })}
                        </div>
                      ) : null}

                      {specialtiesError ? (
                        <p
                          className="agenda-booking-message agenda-booking-message--error"
                          role="alert"
                        >
                          {specialtiesError}
                        </p>
                      ) : null}
                    </div>
                    ) : (
                    <div className="agenda-booking-group">
                      <button
                        type="button"
                        className="agenda-booking-back"
                        onClick={handleBackToSpecialties}
                      >
                        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                          <path
                            d="M15 6l-6 6 6 6"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        Especialidades
                      </button>
                      <p className="agenda-booking-label" id="agenda-specialist-label">
                        Seleccione especialista
                      </p>

                      {(isPresencial ? loadingPresencialSpecialists : loadingSpecialists) ? (
                        <p className="agenda-booking-message" role="status">
                          Cargando especialistas…
                        </p>
                      ) : null}

                      {!(isPresencial ? loadingPresencialSpecialists : loadingSpecialists) &&
                      availableSpecialists.length === 0 ? (
                        <p
                          className="agenda-booking-message agenda-booking-message--warning"
                          role="status"
                        >
                          {isPresencial
                            ? 'Ningún especialista en Terapia coincide con esta sede y especialidad.'
                            : 'Ningún especialista vinculado coincide con esta especialidad.'}
                        </p>
                      ) : null}

                      {availableSpecialists.length > 0 ? (
                        <div
                          className="agenda-specialist-row"
                          role="list"
                          aria-labelledby="agenda-specialist-label"
                        >
                          {availableSpecialists.map((specialist) => {
                            const isSelected =
                              String(selectedSpecialist) === String(specialist.id);
                            const canReview = Boolean(specialist.tiene_resena_publicada);
                            const initials = getSpecialistInitials(specialist);
                            const canSchedule = canScheduleSpecialist(specialist);

                            return (
                              <article
                                key={specialist.id}
                                role="listitem"
                                className={`agenda-specialist-card${
                                  isSelected ? ' agenda-specialist-card--selected' : ''
                                }`}
                              >
                                <div className="agenda-specialist-card-media">
                                  {specialist.avatar_url ? (
                                    <img
                                      src={specialist.avatar_url}
                                      alt=""
                                      className="agenda-specialist-card-photo"
                                    />
                                  ) : (
                                    <span
                                      className="agenda-specialist-card-initials"
                                      aria-hidden="true"
                                    >
                                      {initials}
                                    </span>
                                  )}
                                </div>

                                <strong className="agenda-specialist-card-name">
                                  {specialist.label}
                                </strong>

                                <div className="agenda-specialist-card-actions">
                                  <button
                                    type="button"
                                    className="agenda-specialist-card-btn agenda-specialist-card-btn--review"
                                    onClick={() => handleOpenReview(specialist)}
                                    disabled={!canReview}
                                    title={
                                      canReview
                                        ? 'Ver reseña del especialista'
                                        : 'Este especialista aún no tiene reseña publicada'
                                    }
                                  >
                                    Ver reseña
                                  </button>
                                  <button
                                    type="button"
                                    className="agenda-specialist-card-btn agenda-specialist-card-btn--review"
                                    onClick={() => handleOpenServicios(specialist)}
                                    title="Ver servicios del especialista"
                                  >
                                    Servicios
                                  </button>
                                  <button
                                    type="button"
                                    className="agenda-specialist-card-btn agenda-specialist-card-btn--schedule"
                                    onClick={() => handleContinue(specialist)}
                                    disabled={!canSchedule}
                                  >
                                    Agendar Cita
                                  </button>
                                </div>
                              </article>
                            );
                          })}
                        </div>
                      ) : null}

                      {specialistsError ? (
                        <p
                          className="agenda-booking-message agenda-booking-message--error"
                          role="alert"
                        >
                          {specialistsError}
                        </p>
                      ) : null}

                      {patientsError ? (
                        <p
                          className="agenda-booking-message agenda-booking-message--error"
                          role="alert"
                        >
                          {patientsError}{' '}
                          <Link to="/perfil/pacientes">Registrar paciente</Link>
                        </p>
                      ) : null}

                      {isRepresentante &&
                      !loadingPatients &&
                      patients.length === 0 &&
                      !patientsError ? (
                        <p
                          className="agenda-booking-message agenda-booking-message--warning"
                          role="status"
                        >
                          Debes registrar al paciente al que deseas generar la terapia o cita.{' '}
                          <Link to="/perfil/pacientes">Ir a Pacientes</Link>
                        </p>
                      ) : null}
                    </div>
                    )}
                  </>
                ) : null}
              </>
            ) : null}
            </>
            )}
          </div>
        </div>
      </section>

      {reviewOpen ? (
        <div
          className="agenda-review-modal-backdrop"
          onClick={handleCloseReview}
          role="presentation"
        >
          <div
            className="agenda-review-modal"
            onClick={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="agenda-review-title"
          >
            <div className="agenda-review-modal-header">
              <h2 id="agenda-review-title">Reseña del especialista</h2>
              <button
                type="button"
                className="agenda-review-modal-close"
                onClick={handleCloseReview}
              >
                Cerrar
              </button>
            </div>
            {reviewLoading ? (
              <p className="agenda-booking-message">Cargando reseña…</p>
            ) : null}
            {!reviewLoading && reviewError ? (
              <p className="agenda-booking-message agenda-booking-message--error" role="alert">
                {reviewError}
              </p>
            ) : null}
            {!reviewLoading && !reviewError && reviewData ? (
              <ProfessionalReviewDisplay
                review={reviewData}
                specialistName={reviewSpecialistName}
                specialistAvatarUrl={reviewAvatarUrl}
                specialistInitials={reviewSpecialistInitials}
              />
            ) : null}
          </div>
        </div>
      ) : null}

      {serviciosOpen ? (
        <div
          className="agenda-review-modal-backdrop"
          onClick={handleCloseServicios}
          role="presentation"
        >
          <div
            className="agenda-review-modal agenda-servicios-modal"
            onClick={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="agenda-servicios-title"
          >
            <div className="agenda-review-modal-header">
              <h2 id="agenda-servicios-title">
                Servicios
                {serviciosTarget ? ` · ${serviciosTarget.label}` : ''}
              </h2>
              <button
                type="button"
                className="agenda-review-modal-close"
                onClick={handleCloseServicios}
              >
                Cerrar
              </button>
            </div>
            {serviciosLoading ? (
              <p className="agenda-booking-message">Cargando servicios…</p>
            ) : null}
            {!serviciosLoading && serviciosError ? (
              <p className="agenda-booking-message agenda-booking-message--error" role="alert">
                {serviciosError}
              </p>
            ) : null}
            {!serviciosLoading && !serviciosError && serviciosData.length === 0 ? (
              <p className="agenda-booking-message agenda-booking-message--warning" role="status">
                Este especialista no tiene servicios configurados.
              </p>
            ) : null}
            {!serviciosLoading && !serviciosError && serviciosData.length > 0 ? (
              <ul className="agenda-servicios-list">
                {serviciosData.map((servicio) => (
                  <li key={servicio.id} className="agenda-servicios-item">
                    <div>
                      <strong>{servicio.nombre}</strong>
                      <span>{agendaCitaTipoLabel(servicio.tipo_cita)}</span>
                    </div>
                    <em>
                      {formatAgendaPrecio(servicio.precio, servicio.moneda) ||
                        'Sin precio en inventario'}
                    </em>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default AgendaBookingPage;
